<template>
  <section class="shift-table">
    <div class="table-desc">
      <div class="title">{{ $t('shift_parameters') }}</div>
      <div class="buttons">
        <Button :isClean="true" @click="openModal" skin="primary" :dense="true">{{ $t('add_shift') }}</Button>
        <Button :isClean="true" @click="addReflactiveShifts" skin="primary" :disabled="isProcessingReflactiveShifts" :dense="true">
          <div class="inactive-button">
            <span>{{ $t('add_shift_inactive') }}</span>
            <Transition name="slide-fade">
              <md-progress-spinner v-if="isProcessingReflactiveShifts && !isRemovingReflactiveShifts" :md-diameter="20" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
            </Transition>
          </div>
        </Button>
        <Button class="delete" v-if="isAutomatedRelactiveShifts" :title="$t('remove_auto_shift_inactive')" :isClean="true" @click="removeReflactiveShifts" :dense="true">
          <div class="inactive-button">
            <img :src="require('@/assets/delete-icon.svg')" :class="{ grayed: !isAutomatedRelactiveShifts || isProcessingReflactiveShifts }" alt="remove-icon" />
            <Transition name="slide-fade">
              <md-progress-spinner v-if="isRemovingReflactiveShifts" class="md-accent " :md-diameter="20" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
            </Transition>
          </div>
        </Button>
      </div>
    </div>
    <Table :tableData="i18nShiftData" class="shift-params-table" headerFontSize="11px" bodyFontSize="15px" :editOptions="{ edit: permitToEdit, remove: permitToEdit }" @editRow="openEditModal" @removeRow="removeShift" idField="id" :customRowGridStr="'80px 2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 50px 50px'" />

    <Modal :parent="shiftParentType" width="small" v-if="isAddingShift" @close="closeModal" :texts="{ headerPrimary: $t(headerTxt), headerSecondary: `/ ${companyName}` }" @submit="submitShift" :isConfirmDisabled="isConfirmDisabled">
      <div class="form-container">
        <div class="input-container" v-for="(formItem, index) in shiftForm.filter(({ type }) => type === 'primary')" :key="index" :style="{ 'grid-column-start': index < 4 ? 'span 3' : 'span 4' }">
          <div class="input-label">{{ $t(formItem.label) }}</div>
          <ShiftTimeSelect v-if="formItem.keyInShift === 'start_hour' || formItem.keyInShift === 'end_hour'" v-model="shift[formItem.keyInShift]" :forEndTime="formItem.keyInShift === 'end_hour'" />
          <Input v-else class="input" :type="formItem.inputType" :step="formItem.step" v-model="shift[formItem.keyInShift]" :style="{ marginBottom: 0 }" />
          <ErrorMsg :isShown="errors.includes(formItem.keyInShift)" :text="`${$t('please_enter')} ${$t(formItem.label)}`" />
        </div>
        <div class="days-of-week">
          <div class="input-label">{{ $t('days') }}</div>
          <div v-for="(day, index) in daysOfWeek" :key="index" class="checkbox-container">
            <label> <input type="checkbox" class="days" @click="daySelected($event.target.checked, day.value)" :checked="isChecked(day.value)"/></label>
            <span>{{ $t(`weekMap[${day.value}]`) }}</span>
          </div>
          <ErrorMsg :isShown="errors.includes('week_arrivals')" :text="`${$t('please_enter')} ${$t('days')}`" />
        </div>
        <div class="input-container active-calc-container">
          <label><input type="checkbox" v-model="shift.is_active" class="active-calc"/></label>
          {{ $t(`active_calculation`) }}
        </div>
        <div v-if="!roomId" class="multiselect-container">
          <transition name="fade">
            <label class="field full">
              <span>{{ $t('shift_rooms') }}</span>
              <div class="multi-select">
                <MultiSelectDialog v-if="openSelectMenu" :data="rooms" :initialSelected="selectedRooms" fieldName="name" idFieldName="id" @selected="handleMultiSelectInput($event)" @close="toggleSelect()" />
                <div @click="toggleSelect()" class="multi-select-input">
                  <template v-for="item in selectedRooms">
                    <span @click.stop :key="item.id" class="item" v-if="item.name !== 'All'">
                      <span>{{ item.name }}</span>
                      <span class="close-btn" @click.stop="removeItemFromSelected('selectedRooms', item.id)">x</span>
                    </span>
                  </template>
                </div>
              </div>
            </label>
          </transition>
        </div>
        <template v-if="isMoreOptions">
          <Transition name="fade" appear :duration="{ enter: 500, leave: 200 }">
            <div class="trans-container">
              <div class="input-container" v-for="(formItem, index) in shiftForm.filter(({ type }) => type !== 'primary')" :key="'input' + index" :style="{ 'grid-column-start': index < 4 ? 'span 3' : 'span 4' }">
                <div class="input-label">
                  {{ $t(formItem.label) }}
                  <span class="label-info" :title="$t('tooltip_' + formItem.label)">i</span>
                </div>
                <ShiftTimeSelect v-if="formItem.inputType === 'time_select'" v-model="shift[formItem.keyInShift]" :allowNone="true" :noneLabel="$t('no_auto_close')" />
                <Input v-else class="input" :type="formItem.inputType" :step="formItem.step" v-model="shift[formItem.keyInShift]" :style="{ marginBottom: 0 }" />
                <ErrorMsg :isShown="errors.includes(formItem.keyInShift)" :text="`${$t('please_enter')} ${$t(formItem.label)}`" />
              </div>
            </div>
          </Transition>
        </template>
      </div>
      <template v-slot:footer>
        <Button :isClean="true" @click="isMoreOptions = !isMoreOptions" skin="primary" :isOpen="isMoreOptions">{{ $t('additional_options') }}</Button>
      </template>
    </Modal>
  </section>
</template>

<script>
import shiftService from '../services/shiftService'
import swalService from '@/modules/common/services/swalService'
import ErrorMsg from '@/modules/common/components/ErrorMsg'
import Table from '@/modules/common/components/customTable/Table'
import MultiSelectDialog from '@/modules/common/components/MultiSelectDialog'
import Input from '@/modules/common/components/Input'
import ShiftTimeSelect from '@/modules/common/components/ShiftTimeSelect'
import { shiftForm, daysOfWeek, shiftColsToShow } from '../consts'
import Modal from '@/modules/common/components/Modal'
import roomService from '../services/roomService'
import siteService from '../services/siteService'
import Checkbox from '../../common/components/Checkbox.vue'
import Button from '../../common/components/Button.vue'

export default {
  props: {
    shiftParentType: {
      type: String,
      required: true,
      validator: function(value) {
        // The value must match one of these strings
        return ['room', 'site', 'shift'].includes(value)
      },
    },
    siteId: { type: Number, required: true },
    companyId: { type: Number, required: true },
    roomId: { type: Number, required: false },
    companyName: { type: String, required: false },
  },
  data() {
    return {
      shift: this.getDefaultShift(),
      isAddingShift: false,
      shiftData: [],
      i18nShiftData: [],
      shiftForm,
      daysOfWeek,
      errors: [],
      headerTxt: '',
      openSelectMenu: false,
      rooms: [],
      selectedRooms: [],
      shiftRoomIds: [],
      isConfirmDisabled: false,
      isMoreOptions: false,
      isProcessingReflactiveShifts: false,
      isRemovingReflactiveShifts: false,
    }
  },
  async created() {
    await this.updateViewData()
  },
  methods: {
    formatDecimalHour(decimal) {
      if (decimal === undefined || decimal === null || decimal === '') return ''
      const n = Number(decimal)
      if (isNaN(n)) return ''
      const hours = Math.floor(n)
      const minutes = Math.round((n - hours) * 60)
      if (hours === 24 && minutes === 0) return '24:00'
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
    getShiftValidationErrorMessage(err) {
      const code = err.validationCode
      const details = err.validationDetails || {}
      if (code === 'shift_validation_overlap') {
        const roomName = details.conflictingRoomName
          || this.rooms.find((room) => +room.id === +details.conflictingRoomId)?.name
          || details.conflictingRoomId
        return this.$t('shift_validation_overlap', {
          shiftName: details.conflictingShiftName || details.conflictingShiftId,
          roomName,
        })
      }
      const knownCodes = [
        'shift_validation_missing_fields',
        'shift_validation_empty_rooms',
        'shift_validation_no_valid_rooms',
        'shift_validation_empty_room_map',
      ]
      if (knownCodes.includes(code)) return this.$t(code)
      return err.message || this.$t('Something went wrong!')
    },
    async updateViewData() {
      this.shiftRoomIds = []
      let res = await shiftService.getShifts(this.siteId, this.roomId)
      const rooms = await roomService.getRooms({ site: this.siteId })

      if (!res.length)
        res = [
          {
            arrivals_percent: '',
            complaints_percent: '',
            day_arrivals: '',
            week_arrivals: '[]',
            sla_percent: '',
            end_hour: '',
            max_sla: '',
            min_sla: '',
            start_hour: '',
            id: '',
            shift_name: 'placeholder',
            shift_number: '',
          },
        ]

      this.rooms = rooms.map(({ id, display_name }) => ({ id, name: display_name }))
      this.shiftData = JSON.parse(JSON.stringify(res)).map((item) => {
        if (item.rooms_ids && item.rooms_ids.length) {
          this.shiftRoomIds = [...this.shiftRoomIds, ...item.rooms_ids]
        }
        item.week_arrivals = JSON.parse(item.week_arrivals)
        return item
      })
      res = res.map((item) => {
        const { id } = item
        const sortedItem = {}
        shiftColsToShow.forEach((key) => {
          if (key === 'start_hour' || key === 'end_hour') {
            sortedItem[key] = this.formatDecimalHour(item[key])
          } else {
            sortedItem[key] = item[key]
          }
        })

        for (const property in sortedItem) {
          if (shiftColsToShow.includes(property)) {
            sortedItem[this.$t(property)] = sortedItem[property]
          }
          delete sortedItem[property]
        }
        return { ...sortedItem, id }
      })

      this.i18nShiftData = res
      this.selectedRooms = []
      this.$emit('loaded')
    },
    openEditModal(item) {
      const shiftToEdit = this.shiftData.find((a) => a.id === item.id)
      this.shift = { ...shiftToEdit }
      if (this.shift.day_arrivals === 0) this.shift.day_arrivals = '0'
      if (this.shift.save_arrivals === 0) this.shift.save_arrivals = '0'
      if (this.shift.clean_time === 0) this.shift.clean_time = '0'
      if (this.shift.rooms_ids && this.shift.rooms_ids.length && this.rooms.length) {
        const mappedIds = this.shift.rooms_ids.reduce((acc, curr) => ((acc[curr] = true), acc), {})
        this.rooms.forEach((room) => {
          if (mappedIds[room.id]) this.selectedRooms.push(room)
        })
      }
      this.headerTxt = 'edit_shift'
      this.isAddingShift = true
    },
    openModal() {
      this.shift = this.getDefaultShift()
      this.headerTxt = 'new_shift'
      this.isAddingShift = true
    },
    closeModal(headerTxt) {
      this.shift = this.getDefaultShift()
      this.errors = []
      this.selectedRooms = []
      this.isAddingShift = false
      this.openSelectMenu = false
    },
    daySelected(checkboxValue, dayValue) {
      const dayIndex = this.shift.week_arrivals.indexOf(dayValue)
      const isSelected = dayIndex !== -1
      if (isSelected) this.shift.week_arrivals.splice(dayIndex, 1)
      else this.shift.week_arrivals.push(dayValue)
    },
    isChecked(dayValue) {
      if (this.shift.week_arrivals.includes(dayValue)) return true
      else return false
    },
    async removeShift(id) {
      const result = await swalService.confirmMsg()
      if (!result.value) return
      let roomId = null
      if (this.roomId && this.shiftRoomIds.length) {
        const shiftPerRoom = this.shiftRoomIds.some((id) => +id === +this.roomId)
        if (shiftPerRoom) roomId = this.roomId
      }
      try {
        await shiftService.removeShift(id, roomId)
        swalService.deletedMsg()
        this.updateViewData()
      } catch (err) {
        swalService.errorMsg()
      }
    },
    checkForm() {
      const { week_arrivals, shift_name, shift_number, start_hour, end_hour, min_sla, max_sla, day_arrivals, save_arrivals, clean_time, complaints_percent, sla_percent, arrivals_percent } = this.shift
      this.errors = []
      if (!week_arrivals.length) this.errors.push('week_arrivals')
      if (!shift_name) this.errors.push('shift_name')
      if (!shift_number) this.errors.push('shift_number')
      const quarterHour = 0.25
      if (start_hour < 0 || start_hour > 24 || (start_hour % quarterHour !== 0 && start_hour !== 24)) this.errors.push('start_hour')
      if (!start_hour && start_hour !== 0) this.errors.push('start_hour')
      if (end_hour < 0 || end_hour > 24 || (end_hour % quarterHour !== 0 && end_hour !== 24)) this.errors.push('end_hour')
      if (!end_hour && end_hour !== 0) this.errors.push('end_hour')
      if (!min_sla) this.errors.push('min_sla')
      if (!max_sla) this.errors.push('max_sla')
      const dayArrivalsNum = Number(day_arrivals)
      if (day_arrivals === '' || day_arrivals === undefined || day_arrivals === null || isNaN(dayArrivalsNum) || dayArrivalsNum < 0) this.errors.push('day_arrivals')
      const saveArrivalsNum = Number(save_arrivals)
      if (save_arrivals === '' || save_arrivals === undefined || save_arrivals === null || isNaN(saveArrivalsNum) || saveArrivalsNum < 0) this.errors.push('save_arrivals')
      const cleanTimeNum = Number(clean_time)
      if (clean_time === '' || clean_time === undefined || clean_time === null || isNaN(cleanTimeNum) || cleanTimeNum < 0) this.errors.push('clean_time')
      if (!complaints_percent && complaints_percent !== 0) this.errors.push('complaints_percent')
      if (!sla_percent && sla_percent !== 0) this.errors.push('sla_percent')
      if (!arrivals_percent && arrivals_percent !== 0) this.errors.push('arrivals_percent')
    },
    async submitShift() {
      this.checkForm()
      if (this.errors.length) return
      this.isConfirmDisabled = true
      if (!this.roomId) this.shift.rooms_shift = this.selectedRooms.length ? this.selectedRooms : this.rooms
      const numericKeys = ['shift_number', 'start_hour', 'end_hour', 'min_sla', 'max_sla', 'day_arrivals', 'save_arrivals', 'clean_time', 'complaints_percent', 'sla_percent', 'arrivals_percent', 'complaints_percent_no_visits', 'sla_percent_no_visits', 'arrivals_percent_no_visits', 'cleaners_number']
      const shiftToSave = { ...this.shift }
      numericKeys.forEach((key) => {
        if (shiftToSave[key] !== undefined && shiftToSave[key] !== '') shiftToSave[key] = Number(shiftToSave[key])
      })
      if (shiftToSave.auto_close_complaint_sla_utc_hour === '' || shiftToSave.auto_close_complaint_sla_utc_hour === undefined) {
        shiftToSave.auto_close_complaint_sla_utc_hour = null
      }
      if (shiftToSave.auto_close_maintenance_complaint_sla_utc_hour === '' || shiftToSave.auto_close_maintenance_complaint_sla_utc_hour === undefined) {
        shiftToSave.auto_close_maintenance_complaint_sla_utc_hour = null
      }
      try {
        await shiftService.saveShift(shiftToSave)
        this.isConfirmDisabled = false
        swalService.savedMsg(this.$t('Your work has been saved'))
        this.updateViewData()
      } catch (err) {
        swalService.errorMsg(this.getShiftValidationErrorMessage(err))
        this.isConfirmDisabled = false
      }
      this.isAddingShift = false
    },
    handleMultiSelectInput(values, field) {
      if (!values) return
      this.selectedRooms = values
    },
    toggleSelect() {
      this.openSelectMenu = !this.openSelectMenu
    },
    removeItemFromSelected(objName, id) {
      const idx = this[objName].findIndex((item) => item.id === id)
      if (idx !== -1) this[objName].splice(idx, 1)
    },
    getDefaultShift() {
      return {
        week_arrivals: [],
        room_id: this.roomId,
        site_id: this.siteId,
        company_id: this.companyId,
        is_active: true,
        min_sla: '15',
        max_sla: '60',
        day_arrivals: '3',
        save_arrivals: '3',
        clean_time: '15',
        complaints_percent: '35',
        sla_percent: '35',
        arrivals_percent: '30',
        complaints_percent_no_visits: '20',
        sla_percent_no_visits: '60',
        arrivals_percent_no_visits: '20',
        shift_number: this?.shiftData?.[0].shift_name === 'placeholder' ? 1 : this?.shiftData?.length + 1,
        cleaners_number: 1,
        auto_close_complaint_sla_utc_hour: null,
        auto_close_maintenance_complaint_sla_utc_hour: null,
      }
    },
    async addReflactiveShifts() {
      try {
        this.isProcessingReflactiveShifts = true
        await siteService.addReflactiveShifts(this.siteId, this.companyId, this.language)
        await this.updateViewData()
        this.isProcessingReflactiveShifts = false
        swalService.savedMsg(this.$t('Your work has been saved'))
      } catch (error) {
        this.isProcessingReflactiveShifts = false
        swalService.errorMsg('Could not add reflactive shifts.')
      }
    },
    async removeReflactiveShifts() {
      try {
        this.isProcessingReflactiveShifts = true
        this.isRemovingReflactiveShifts = true
        await siteService.removeReflactiveShifts(this.siteId, this.companyId)
        await this.updateViewData()
        this.isProcessingReflactiveShifts = false
        this.isRemovingReflactiveShifts = false
        swalService.savedMsg(this.$t('Your work has been saved'))
      } catch (error) {
        this.isProcessingReflactiveShifts = false
        this.isRemovingReflactiveShifts = false
        swalService.errorMsg('Could not delete reflactive shifts.')
      }
    },
  },
  computed: {
    language() {
      return JSON.parse(localStorage.getItem('language') || 'en')
    },
    permitToEdit() {
      if (!this.roomId) return true
      else if (this.shiftRoomIds.length && this.roomId) {
        return this.shiftRoomIds.includes(+this.roomId)
      } else return true
    },
    isAutomatedRelactiveShifts() {
      return this?.shiftData.some(({ isAutomated }) => isAutomated)
    },
  },
  components: {
    Modal,
    Input,
    ShiftTimeSelect,
    Table,
    ErrorMsg,
    MultiSelectDialog,
    Checkbox,
    Button,
  },
  watch: {
    siteId: {
      handler() {
        this.updateViewData()
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss">
.shift-table {
  .secondary-title {
    font-size: 24px !important;
  }
}

#app:has(.days-of-week) .search-bar .md-field {
  margin: unset;
  input {
    max-height: unset !important;
    height: unset !important;
  }
}

#app:has(.days-of-week) .md-field input {
  min-height: 40px !important;
  display: flex !important;
}

.buttons {
  .delete {
    background-color: #f1f1f1 !important;
  }

  .md-button:hover:before {
    opacity: 0.05;
  }
}
</style>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;
$table-grey: #7f7f7f;
.shift-table {
  color: #7f7f7f;
  margin-inline-end: 10px;
  // text-transform: capitalize;
  display: flex;
  flex-direction: column;
  .table-desc {
    display: flex;
    justify-content: space-between;
    margin-bottom: 7px;

    .title {
      color: $table-grey;
      font-weight: bold;
    }

    .buttons {
      display: flex;
      gap: 15px;
    }

    .add-button {
      cursor: pointer;
      color: $blue;
      line-height: unset;
    }
  }
  .shift-params-table {
    border: 1px solid #e7e7e7;
    overflow-y: scroll;
    flex-grow: 1;
    @include scroll;
  }

  .shift-holiday & {
    flex-basis: 250%;
  }
}

.form-container {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: 1fr 1fr 1fr;

  .input-label {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .label-info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #9e9e9e;
    color: #fff;
    font-size: 11px;
    font-style: italic;
    font-weight: bold;
    cursor: help;
    flex-shrink: 0;
  }

  .label-info:hover {
    background: #616161;
  }

  .input-container {
    margin-inline-end: 6px;
    &.active-calc-container {
      grid-column: 1/5;
      grid-row: 4/5;
    }

    .input {
      border: 1px solid $grey-border-color;
      border-radius: 4px;
      padding: 0 10px;
      height: 46px;
    }
  }

  .days-of-week {
    grid-column: 1/5;
    grid-row: 2 / 4;
    &,
    .input-container {
      margin-bottom: 20px;
    }
  }
}

.checkbox-container,
.active-calc-container {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 23px;

  width: unset;
  height: unset;
  border: unset;
  background-color: unset;
  border-radius: unset;
  margin: unset;
  justify-content: unset;

  input.days,
  input.active-calc {
    display: none;
  }

  label:has(input.days, input.active-calc) {
    display: inline-block;
    width: 15px;
    height: 15px;
    border: 1px solid #e7e7e7;
    border-radius: 4px;
    cursor: pointer;
  }

  label:has(input.days, input.active-calc)::after {
    position: relative;
    display: flex;
    width: 15px;
    height: 15px;
    content: '\2714';
    font-size: 13px;
    color: #fff;
    border-radius: 4px;
    background-color: rgba(0, 130, 243, 1);
    align-items: center;
    justify-content: center;
    margin-inline-start: -1px;
    margin-block-start: -1px;
    opacity: 0;
    padding-block-start: 1px;
    transition: opacity 0.2s ease-in-out;
  }

  label:has(input.days:checked, input.active-calc:checked)::after {
    transition: opacity 0.2s ease-in-out;
    opacity: 1;
  }
}

.multiselect-container {
  grid-column: 4/13;
  grid-row: 2/5;

  .field.full {
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
  }

  .multiselect-dialog {
    height: 200px;
  }

  .multi-select-input {
    cursor: pointer;
    @include scroll;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    border-radius: 4px;
    overflow-x: hidden;
    height: 100%;
    padding-inline: 6px;
    padding-block: 5px;
    .item {
      margin-bottom: 2px;
      margin-top: 2px;
      span {
        max-height: 40px;
      }
      .close-btn {
        margin-inline-start: 20px;
        cursor: pointer;
      }
      margin-inline-end: 7px;
      display: flex;
      align-items: center;
      padding: 10px;
      border-radius: 4px;
      background-color: $grey-border-color;
    }
  }
  .multi-select {
    width: 100%;
    height: 100%;
    position: relative;
    max-height: 200px;
    max-width: 490px;
    @include border($grey-border-color);
    .dialog {
      position: absolute;
      max-width: 300px;
      right: 0;
      @include rtl {
        right: unset;
        left: 0;
      }
    }
  }
}

.trans-container {
  display: flex;
  flex-wrap: wrap;
  grid-column-start: span 12;
  max-width: 665px;
  padding-block-start: 40px;
  padding-block-end: 200px;

  .input-container {
    margin-block-end: 10px;
  }
}

.inactive-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.slide-fade-enter-active {
  transition: all 0.9s linear;
  animation: none;
}

.slide-fade-leave-active {
  transition: all 0.1s linear;
  animation: none;
}

.grayed {
  filter: grayscale(1);
}
</style>
